/**
 * Shared auto-fit layout for Trade Log / Statistics mini-stat grids.
 * Track min grows with the longest title/value so amounts are less likely to clip.
 */

export type MiniStatGridLayoutOptions = {
  /** Lower bound for track min (rem). */
  floor?: number;
  /** Upper bound for track min (rem). */
  ceiling?: number;
  /** Approximate rem per character at mini-stat type size. */
  remPerChar?: number;
  /** Horizontal padding / chrome allowance (rem). */
  paddingRem?: number;
  /**
   * Prefer at most this many columns on wide containers.
   * Uses gap-aware share width so N columns still fit (plain % ignores gaps).
   */
  maxColumns?: number;
  /**
   * Prefer at least this many columns on narrow containers.
   * Caps each track so two (or more) columns still fit on mobile.
   */
  minColumns?: number;
  /** Horizontal gap used by the grid (px). Defaults to 24. */
  gapPx?: number;
};

const DEFAULTS = {
  floor: 13.5,
  ceiling: 22,
  /** When capping columns, keep content mins modest so desktop still hits maxColumns. */
  cappedFloor: 11,
  cappedCeiling: 14,
  remPerChar: 0.78,
  paddingRem: 3,
  gapPx: 24,
} as const;

/** Count numeric digits in a formatted amount string. */
export function miniStatAmountDigitCount(amount: string): number {
  return amount.replace(/\D/g, "").length;
}

/**
 * When true, a mini-stat amount likely needs a full-width row on narrow viewports.
 * Moderate values like "+$20,864.75" still fit in half-width cards on mobile.
 */
export function miniStatAmountNeedsFullWidthOnMobile(amount: string): boolean {
  if (!amount) return false;
  return miniStatAmountDigitCount(amount) >= 9 || amount.length >= 17;
}

export function dashboardMiniStatsGridTemplateColumns(
  items: ReadonlyArray<{ title?: string; amount?: string }>,
  options: { isMobile: boolean; maxColumns?: number; gapPx?: number }
): string {
  const needsFullWidth = items.some(
    (item) => item.amount && miniStatAmountNeedsFullWidthOnMobile(item.amount)
  );

  if (options.isMobile) {
    return needsFullWidth ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))";
  }

  return miniStatGridTemplateColumns(items, {
    maxColumns: options.maxColumns ?? 4,
    gapPx: options.gapPx ?? DEFAULTS.gapPx,
  });
}

export function miniStatGridLayoutOptionsForViewport(
  items: ReadonlyArray<{ title?: string; amount?: string }>,
  options: { isMobile: boolean; maxColumns?: number; gapPx?: number }
): MiniStatGridLayoutOptions {
  const needsFullWidth = items.some(
    (item) => item.amount && miniStatAmountNeedsFullWidthOnMobile(item.amount)
  );

  return {
    maxColumns: options.maxColumns ?? 4,
    gapPx: options.gapPx ?? DEFAULTS.gapPx,
    minColumns: options.isMobile && !needsFullWidth ? 2 : undefined,
  };
}

/** Longest string length across titles and amounts. */
export function longestMiniStatTextLength(
  items: ReadonlyArray<{ title?: string; amount?: string } | string>
): number {
  let max = 0;
  for (const item of items) {
    if (typeof item === "string") {
      max = Math.max(max, item.length);
      continue;
    }
    max = Math.max(max, item.title?.length ?? 0, item.amount?.length ?? 0);
  }
  return max;
}

/** Content-aware minimum column track width in rem. */
export function miniStatGridTrackMinRem(
  items: ReadonlyArray<{ title?: string; amount?: string } | string>,
  options?: MiniStatGridLayoutOptions
): number {
  const capped = options?.maxColumns != null && options.maxColumns > 0;
  const floor = options?.floor ?? (capped ? DEFAULTS.cappedFloor : DEFAULTS.floor);
  const ceiling = options?.ceiling ?? (capped ? DEFAULTS.cappedCeiling : DEFAULTS.ceiling);
  const remPerChar = options?.remPerChar ?? DEFAULTS.remPerChar;
  const paddingRem = options?.paddingRem ?? DEFAULTS.paddingRem;
  const longest = longestMiniStatTextLength(items);
  const fromContent = longest * remPerChar + paddingRem;
  return Math.min(ceiling, Math.max(floor, fromContent));
}

/** `grid-template-columns` value for auto-fit mini-stat grids. */
export function miniStatGridTemplateColumns(
  items: ReadonlyArray<{ title?: string; amount?: string } | string>,
  options?: MiniStatGridLayoutOptions
): string {
  const minRem = miniStatGridTrackMinRem(items, options);
  const gapPx = options?.gapPx ?? DEFAULTS.gapPx;
  const maxColumns = options?.maxColumns;
  const minColumns = options?.minColumns;

  const trackFloor =
    maxColumns != null && maxColumns > 0
      ? `max(${minRem}rem, calc((100% - ${Math.max(0, maxColumns - 1) * gapPx}px) / ${maxColumns}))`
      : `${minRem}rem`;

  // Cap track width so at least minColumns fit (avoids collapsing to 1 col on mobile).
  const trackCap =
    minColumns != null && minColumns > 1
      ? `calc((100% - ${Math.max(0, minColumns - 1) * gapPx}px) / ${minColumns})`
      : "100%";

  return `repeat(auto-fit, minmax(min(${trackCap}, ${trackFloor}), 1fr))`;
}
